import {Injectable} from '@angular/core';
import {BatchDisplayStatus, BatchStatus} from 'web-angular-client';

/**
 * Utility class for providing some user-friendly transformations to a {@link BatchStatus}.
 */
@Injectable({
    providedIn: 'root'
})
export class BatchStatusUtil
{

    public OPEN = BatchDisplayStatus.OPEN;
    public CLOSED = BatchDisplayStatus.CLOSED;
    public SETTLED = BatchDisplayStatus.SETTLED;
    public ANY = BatchDisplayStatus.ANY;

    public getShortDescription(batchStatus: BatchStatus): string
    {
        switch (batchStatus)
        {
            case BatchStatus.OPEN:
                return this.OPEN;
            case BatchStatus.SETTLED :
                return this.SETTLED;
            case BatchStatus.CLOSED :
                return this.CLOSED;
            default:
                return 'Unknown';
        }
    }

    public getBatchStatusFromDescription(description: string): Array<BatchStatus>
    {
        switch (description)
        {
            case this.OPEN:
                return [BatchStatus.OPEN];
            case this.CLOSED:
                return [BatchStatus.CLOSED];
            case this.SETTLED:
                return [BatchStatus.SETTLED];
            default:
                return null;
        }
    }

    public getDisplayStatusFromDescription(description: string)
    {
        switch (description)
        {
            case this.OPEN:
                return BatchDisplayStatus.OPEN;
            case this.CLOSED:
                return BatchDisplayStatus.CLOSED;
            case this.SETTLED:
                return BatchDisplayStatus.SETTLED;
            default:
                return null;
        }
    }

    public getLongDescription(batchStatus: BatchStatus): string
    {
        switch (batchStatus)
        {
            case BatchStatus.CLOSED:
                return 'Payments in this batch have been processed and the settlement amount will be paid in the next ' +
                    'settlement billing cycle. Settlement batches are only closed if bank details have been verified.';
            case BatchStatus.OPEN:
                return 'The current batch of payments has not yet been processed for settlement.';
            case BatchStatus.SETTLED:
                return 'The total settlement amount for this batch has been paid. It may take 1-2 working days for the ' +
                    'amount to reflect in your account.';
            default:
                return 'The current batch is in an unknown status.';
        }
    }

    /**
     * @return the {@link getShortDescription} and {@link getLongDescription} of each batch status, separated by a
     * new line.
     */
    public getDescriptionOfAllStatuses(): string
    {
        return this.getShortDescription(BatchStatus.OPEN) + ': ' + this.getLongDescription(BatchStatus.OPEN) + '\n' +
            this.getShortDescription(BatchStatus.CLOSED) + ': ' + this.getLongDescription(BatchStatus.CLOSED) + '\n' +
            this.getShortDescription(BatchStatus.SETTLED) + ': ' + this.getLongDescription(BatchStatus.SETTLED);
    }

    /**
     * @return {@code true} if {@link BatchStatus} is {@link BatchStatus#CLOSED} or {@link BatchStatus#SETTLED}.
     *     {@code false} otherwise.
     */
    isClosedSettlementBatch(batchStatus: BatchStatus): boolean
    {
        return (batchStatus !== null && (batchStatus === BatchStatus.CLOSED || batchStatus === BatchStatus.SETTLED));
    }

    /**
     * This method is used in combination with the style or ngStyle directive to display {@link BatchStatus} as
     * different colour text in an html element.
     *
     * @return the text colour that represents the {@link BatchStatus}, as a string.
     */
    public getTextColor(batchStatus: BatchStatus): string
    {
        if (batchStatus == null)
        {
            return 'black';
        }

        switch (batchStatus)
        {
            case BatchStatus.CLOSED:
                return 'blue';
            case BatchStatus.SETTLED:
                return 'green';
            default:
                return 'black';
        }
    }
}